import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { IconPrinterOutline } from '@icons/IconPrinterOutline'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { MdTextAction } from '../types'
import { ActionItemNoConfirmation } from './ActionItemNoConfirmation'
import { ActionWithConfirmDialog } from './ActionWithConfirmDialog'

export function ActionsMenu({
  actions,
  openEditing,
  markdown,
  reloadHandler,
  onExportPdf,
  hideInlineEdit = false,
}: {
  actions: MdTextAction[]
  openEditing: () => void
  markdown: string
  reloadHandler: () => void
  onExportPdf?: () => void
  hideInlineEdit?: boolean
}) {
  const visibleActions = actions.filter((action) => action.visible)
  // const canEditApp = useCanEditApp()

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          // This is necessary to maintain the open state when we want to keep it open
          if (!open) {
            // Check if we need to prevent closing
            // This is a placeholder for custom logic you might need
            // We'll use other mechanisms to prevent closing on item click
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8 rounded-full">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => {
            // Prevent focus returning to trigger which can close the menu
            e.preventDefault()
          }}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {visibleActions.map((action) => {
            if (action.type === 'edit' || action.id === 'edit') {
              if (hideInlineEdit) {
                return <EditMenuItem action={action} openEditing={openEditing} key={action.id} label="Edit in viewer" />
              }
              return <EditMenuItem action={action} openEditing={openEditing} key={action.id} />
            }
            if (!action.confirmationRequired) {
              return (
                <ActionItemNoConfirmation
                  action={action}
                  markdown={markdown}
                  key={action.id}
                  reloadHandler={reloadHandler}
                />
              )
            }
            return (
              <DropdownMenuItem key={action.id} onSelect={(e) => e.preventDefault()}>
                <ActionWithConfirmDialog action={action} markdown={markdown} reloadHandler={reloadHandler} />
              </DropdownMenuItem>
            )
          })}
          {visibleActions.length > 0 && onExportPdf && <DropdownMenuSeparator />}
          {onExportPdf && (
            <DropdownMenuItem
              onSelect={() => {
                onExportPdf()
              }}
            >
              <div className="flex items-center gap-2">
                <IconPrinterOutline className="size-3" />
                <span>Export PDF</span>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

function EditMenuItem({
  action,
  openEditing,
  label,
}: {
  action: MdTextAction
  openEditing: () => void
  label?: string
}) {
  return (
    <DropdownMenuItem key={action.id} onSelect={openEditing}>
      <div className="flex items-center gap-2">
        <DynamicIcon
          iconName={action.icon || ''}
          lazyLoad={false}
          className="size-3"
          fallback={<div className="size-4" />}
        />
        <span>{label ?? action.label}</span>
      </div>
    </DropdownMenuItem>
  )
}
